/* workspace-admin/Membership — add/remove members, set workspace role.
   Manage actions gated to membership.manage. Clean ESM. */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Btn, Pill, EmptyState, Modal } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { Gate } from '../rbac/guards';
import { useRbac } from '../rbac/RbacContext';
import { StatCard, PageHead, Th } from '../platform/_ui';
import { findWorkspace, workspaceMembers, orgMembers } from '../platform/platformStore';
import { ORG_ROLE_DISPLAY } from './workspaceConstants.js';
import * as workspaceApi from '../api/workspace.api.js';

const WS_ROLES = ['Workspace Admin', 'Workspace User'];
const WS_ROLE_ENUM = { 'Workspace Admin': 'WORKSPACE_ADMIN', 'Workspace User': 'WORKSPACE_USER' };
const WS_ROLE_DISPLAY = { WORKSPACE_ADMIN: 'Workspace Admin', WORKSPACE_USER: 'Workspace User' };
const toDisplayRole = r => WS_ROLE_DISPLAY[r] || r;

export default function Membership({ data }) {
  const { wsId } = useParams();
  const toast = useToast();
  const { can } = useRbac();
  const [adding, setAdding] = useState(false);
  const [addedLink, setAddedLink] = useState(null);
  const [pending, setPending] = useState(new Set());
  const ws = findWorkspace(data.state, wsId);
  if (!ws) return null;

  const members = workspaceMembers(data.state, wsId);
  const memberIds = new Set(members.map(m => m.memberId));
  const assignable = orgMembers(data.state, ws.org).filter(m => !memberIds.has(m.id));
  const canManage = can('membership.manage');

  const setBusy = (id, busy) => setPending(p => { const s = new Set(p); busy ? s.add(id) : s.delete(id); return s; });

  const addMember = async (member, wsRole) => {
    setBusy(member.id, true);
    try {
      const res = await workspaceApi.addMember(wsId, { memberId: member.id, wsRole: WS_ROLE_ENUM[wsRole] || wsRole });
      const id = res?.id || ('mb_' + Date.now() + '_' + member.id);
      data.update(s => ({
        ...s,
        memberships: [...s.memberships, { id, workspace: wsId, memberId: member.id, wsRole: WS_ROLE_ENUM[wsRole] || wsRole, status: 'ACTIVE' }],
        workspaces: s.workspaces.map(w => w.id === wsId ? { ...w, members: w.members + 1 } : w),
      }));
      window.dispatchEvent(new CustomEvent('nx-members-changed', { detail: { workspaceId: wsId } }));
      data.addAudit({ action: 'added member', target: ws.name + ' · ' + member.name, cat: 'Org' });
      setAdding(false);
      setAddedLink({ name: member.name, loginUrl: window.location.origin });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Add failed', desc: err.message });
    } finally {
      setBusy(member.id, false);
    }
  };

  const removeMember = async (mb) => {
    setBusy(mb.id, true);
    try {
      await workspaceApi.removeMember(wsId, mb.memberId);
      data.update(s => ({
        ...s,
        memberships: s.memberships.filter(x => x.id !== mb.id),
        workspaces: s.workspaces.map(w => w.id === wsId ? { ...w, members: Math.max(0, w.members - 1) } : w),
      }));
      window.dispatchEvent(new CustomEvent('nx-members-changed', { detail: { workspaceId: wsId } }));
      data.addAudit({ action: 'removed member', target: ws.name + ' · ' + mb.member.name, cat: 'Org', sev: 'warning' });
      toast({ tone: 'warning', icon: 'Trash', title: 'Member Removed', desc: mb.member.name });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Remove failed', desc: err.message });
    } finally {
      setBusy(mb.id, false);
    }
  };

  const setRole = async (mb, wsRole) => {
    setBusy(mb.id, true);
    try {
      await workspaceApi.updateMember(wsId, mb.memberId, { wsRole: WS_ROLE_ENUM[wsRole] || wsRole });
      data.update(s => ({ ...s, memberships: s.memberships.map(x => x.id === mb.id ? { ...x, wsRole: WS_ROLE_ENUM[wsRole] || wsRole } : x) }));
      toast({ tone: 'success', icon: 'Shield', title: 'Role Updated', desc: mb.member.name + ' → ' + wsRole });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err.message });
    } finally {
      setBusy(mb.id, false);
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Membership"
        sub={members.length + ' members in ' + ws.name}
        actions={<Gate action="membership.manage"><Btn variant="primary" icon="UserPlus" onClick={() => setAdding(true)}>Add Member</Btn></Gate>}
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <StatCard label="Members"          value={members.length} icon="Users"    color="blue"   />
        <StatCard label="Workspace Admins" value={members.filter(m => m.wsRole === 'WORKSPACE_ADMIN').length} icon="Shield" color="purple" />
        <StatCard label="Available to add" value={assignable.length} sub="from organization" icon="UserPlus" color="green"  />
      </div>

      <div className="nx-table-wrap" style={{ marginTop: 16 }}>
        <div className="nx-table-scroll">
          <table className="nx-table" aria-label="Workspace members">
            <thead><tr>
              <Th icon="User">Member</Th>
              <Th icon="Mail">Email</Th>
              <Th icon="Shield">Workspace Role</Th>
              <Th icon="Activity">Status</Th>
              <Th end />
            </tr></thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan={5}>
                  <EmptyState icon="Users" title="No members yet" desc="Add members from your organization." />
                </td></tr>
              ) : members.map(mb => {
                const isBusy = pending.has(mb.id);
                return (
                  <tr key={mb.id} className="nx-tr" aria-busy={isBusy || undefined}>
                    <td>
                      <span className="nx-row-name">
                        <span className="nx-domain-mark" aria-hidden="true" style={{ width: 24, height: 24, fontSize: 11, background: (mb.member.tone || '#125AB0') + '22', color: mb.member.tone }}>{mb.member.name[0]}</span>
                        <span className="nx-row-name-text">{mb.member.name}</span>
                      </span>
                    </td>
                    <td><span className="nx-muted">{mb.member.email}</span></td>
                    <td>
                      {canManage ? (
                        <select
                          className="nx-input"
                          style={{ height: 32, maxWidth: 190 }}
                          value={toDisplayRole(mb.wsRole)}
                          disabled={isBusy}
                          aria-label={'Workspace role for ' + mb.member.name}
                          onChange={e => setRole(mb, e.target.value)}
                        >
                          {WS_ROLES.map(r => <option key={r}>{r}</option>)}
                        </select>
                      ) : <Pill tone={mb.wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>{toDisplayRole(mb.wsRole)}</Pill>}
                    </td>
                    <td><Pill tone={mb.status === 'ACTIVE' ? 'green' : 'amber'}>{mb.status}</Pill></td>
                    <td className="nx-td-end">
                      <Gate action="membership.manage">
                        <Btn variant="ghost" size="sm" icon="Trash" disabled={isBusy}
                          aria-label={'Remove ' + mb.member.name + ' from workspace'}
                          onClick={() => removeMember(mb)}>
                          Remove
                        </Btn>
                      </Gate>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!canManage && (
        <p className="nx-muted nx-mini" style={{ marginTop: 10 }}>
          You have view-only access. Workspace Admin or higher is required to change membership.
        </p>
      )}

      {adding && (
        <Modal title="Add Members" icon="UserPlus" width={520} onClose={() => setAdding(false)}>
          <p className="nx-confirm-desc">Assign organization members to <strong>{ws.name}</strong>.</p>
          <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {assignable.length === 0 ? (
              <EmptyState icon="Users" title="Everyone is already a member" desc="All organization members have been added to this workspace." />
            ) : assignable.map(m => (
              <div key={m.id} className="nx-ws-assign-row">
                <span className="nx-domain-mark" aria-hidden="true" style={{ width: 26, height: 26, fontSize: 11, background: (m.tone || '#125AB0') + '22', color: m.tone }}>{m.name[0]}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="nx-row-name-text">{m.name}</span>
                  <div className="nx-muted nx-mini">{m.email} · {ORG_ROLE_DISPLAY[m.orgRole] ?? m.orgRole}</div>
                </span>
                <Btn variant="ghost" size="sm" icon="Plus" disabled={pending.has(m.id)}
                  aria-label={'Add ' + m.name + ' as Workspace User'}
                  onClick={() => addMember(m, 'Workspace User')}>User</Btn>
                <Btn variant="secondary" size="sm" icon="Shield" disabled={pending.has(m.id)}
                  aria-label={'Add ' + m.name + ' as Workspace Admin'}
                  onClick={() => addMember(m, 'Workspace Admin')}>Admin</Btn>
              </div>
            ))}
          </div>
          <div className="nx-confirm-actions"><Btn variant="ghost" onClick={() => setAdding(false)}>Done</Btn></div>
        </Modal>
      )}

      {addedLink && (
        <Modal title="Member Added" icon="UserPlus" onClose={() => setAddedLink(null)}>
          <p className="nx-confirm-desc"><strong>{addedLink.name}</strong> has been added to {ws.name}. Share this link so they can log in.</p>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
              <label htmlFor="ws-added-link">App Link</label>
              <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center' }}>
                <input id="ws-added-link" className="nx-input" readOnly value={addedLink.loginUrl}
                  style={{ flex: 1, fontSize: 11, fontFamily: 'monospace' }}
                  onClick={e => e.target.select()} />
                <Btn variant="ghost" size="sm" icon="Copy"
                  aria-label="Copy app link to clipboard"
                  onClick={() => navigator.clipboard.writeText(addedLink.loginUrl).then(() => toast({ tone: 'success', icon: 'Copy', title: 'Link copied' })).catch(() => toast({ tone: 'warning', icon: 'Copy', title: 'Copy failed', desc: 'Select and copy the link manually.' }))}>
                  Copy
                </Btn>
              </div>
              <span className="nx-muted nx-mini">They can log in with their existing credentials.</span>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={() => setAddedLink(null)}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
